import { useState, useLayoutEffect, useRef, useCallback } from "react";

// Editable field hook for managing contentEditable elements with placeholder support
// Adapted from wall-of-love default-template.tsx pattern
export function useEditableField(initialValue: string, onChange: ((value: string) => void) | undefined, placeholder?: string) {
	const ref = useRef<HTMLElement>(null);
	const isEditingRef = useRef(false);
	const previousPropValueRef = useRef<string>(initialValue);
	const lastElementRef = useRef<HTMLElement | null>(null);
	const [isEmpty, setIsEmpty] = useState(!initialValue.trim());

	// Ref callback to detect when DOM element mounts/remounts (e.g., format switch)
	const setRef = useCallback(
		(element: HTMLElement | null) => {
			(ref as React.MutableRefObject<HTMLElement | null>).current = element;
			if (element && element !== lastElementRef.current) {
				// New or remounted element — initialize its content
				element.textContent = initialValue;
				previousPropValueRef.current = initialValue;
				setIsEmpty(!initialValue.trim());
				lastElementRef.current = element;
			}
		},
		[initialValue],
	);

	// Sync content when initialValue prop changes (e.g., discard/reset)
	// Using useLayoutEffect to ensure content is set before browser paints
	useLayoutEffect(() => {
		if (!ref.current) return;

		const currentDomValue = ref.current.textContent || "";

		// Check if the prop value actually changed (detects discard/reset)
		const propValueChanged = previousPropValueRef.current !== initialValue;
		const domDiffersFromProp = currentDomValue !== initialValue;

		// Sync if:
		// - Not editing: sync whenever DOM differs from prop
		// - Editing: sync only if prop changed (handles discard) and DOM doesn't already match
		if ((!isEditingRef.current && domDiffersFromProp) || (isEditingRef.current && propValueChanged && domDiffersFromProp)) {
			ref.current.textContent = initialValue;
			// eslint-disable-next-line react-hooks/set-state-in-effect -- Syncing DOM content with isEmpty state after forced content update
			setIsEmpty(!initialValue.trim());
		}

		// Always update previousPropValueRef to track prop changes accurately
		previousPropValueRef.current = initialValue;
	}, [initialValue]);

	const handlers = {
		onFocus: () => {
			isEditingRef.current = true;
		},
		onBlur: () => {
			isEditingRef.current = false;
			const currentValue = ref.current?.textContent || "";
			setIsEmpty(!currentValue.trim());
		},
		onInput: (e: React.FormEvent<HTMLElement>) => {
			const newValue = e.currentTarget.textContent || "";
			onChange?.(newValue);
			setIsEmpty(!newValue.trim());
		},
		onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
			if (e.key === "Enter" && !e.shiftKey) {
				e.preventDefault();
				e.currentTarget.blur();
			}
		},
		onPaste: (e: React.ClipboardEvent) => {
			e.preventDefault();
			const text = e.clipboardData.getData("text/plain");
			document.execCommand("insertText", false, text);
		},
	};

	return { ref: setRef, handlers, isEmpty, placeholder };
}
